import React, { LegacyRef, useState, FC, useCallback, useRef, useEffect, useMemo } from 'react';
import DropdownMenu from '@atlaskit/dropdown-menu';
import TextField from '@atlaskit/textfield';
import Button from '@atlaskit/button/new';
import { Checkbox } from '@atlaskit/checkbox';
import { Radio } from '@atlaskit/radio';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import SelectClearIcon from '@atlaskit/icon/glyph/select-clear';
import { token } from '@atlaskit/tokens';
import { OptionType } from '@atlaskit/select';
import orderBy from 'lodash/orderBy';
import uniqBy from 'lodash/uniqBy';
import { css } from '@emotion/core';
import Badge from '@atlaskit/badge';
import isNumber from 'lodash/isNumber';

import { flexCenter, flexStart, spaceGapMinimum, spaceTopCozy } from '@/styles';

import {
  advancedDropdownMenuStyles,
  advancedDropdownSearchStyles,
  clearFilterStyles,
  labelSelectedStyles,
  labelSelectedTitleStyles,
  secondaryTextStyles,
} from './styles';
import { AdvancedDropdownProps } from './types';

const AdvancedDropdown: FC<AdvancedDropdownProps> = ({
  label,
  onChange,
  placeholder,
  value,
  isSearchable,
  isMulti,
  isAsync,
  closeMenuOnSelect,
  rerenderMenuOnSelect,
  loadOptions = async () => [],
  options = [],
  menuWidth,
  menuHeight,
  labelReset = 'Clear filter',
  staticTrigger,
  appearance,
  iconAfter = <ChevronDownIcon label="" />,
  hideResetButton,
}) => {
  // eslint-disable-next-line no-nested-ternary
  const selectedOptions = isMulti ? (value ? (value as []) : []) : value ? [value as string] : [];
  const searchRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string | undefined>();
  const [menuOptions, setMenuOptions] = useState<OptionType[]>([]);

  // Rerender to update the new position of the dropdown. This is a workaround
  // to fix the dropdown position issue when the dropdown selection can affect
  // the other dropdown showing in the filter bar.
  const toggleOpen = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      setIsOpen(true);
    }, 0);
  }, []);

  // Get full menu options with:
  // - selected options are always on top
  // - alphabetically ordered
  // - uniq by value
  //
  // This should only be called when the dropdown is open, or when the serach
  // term is completely removed.
  const getFullMenuOptions = useCallback(
    (menuOptionsNext: OptionType[]) => {
      // First merge the selected options with the menu options
      let mergedOptions = [
        ...selectedOptions.map(x => ({ label: x, value: x })),
        ...menuOptionsNext,
      ];
      // Uniq by value
      mergedOptions = uniqBy(mergedOptions, 'value');
      // Order by selected first, then by value
      mergedOptions = orderBy(
        mergedOptions,
        [x => selectedOptions.includes(`${x.value}`), 'value'],
        ['desc', 'asc']
      );
      return mergedOptions;
    },
    [selectedOptions]
  );

  const onRemoveSearchTerm = useCallback(() => {
    setSearchTerm(undefined);
    try {
      (searchRef.current?.querySelector('input') as HTMLInputElement).value = '';
    } catch (_e) {
      // ignore, not a critical behavior
    }
  }, []);

  const handleOptionClick = useCallback(
    (v: string) => {
      let selectedOptionsNext: string[] = isMulti ? selectedOptions : [];
      if (selectedOptionsNext.includes(v)) {
        selectedOptionsNext = selectedOptionsNext.filter(x => x !== v);
      } else {
        selectedOptionsNext.push(v);
      }
      // Sort alphabetically so the order in URL params is consistent.
      // This has nothing to do with the order in the dropdown so far
      selectedOptionsNext = orderBy(selectedOptionsNext, x => x, 'asc');
      onChange(selectedOptionsNext);

      if (closeMenuOnSelect) {
        setIsOpen(false);
      }

      if (rerenderMenuOnSelect) {
        toggleOpen();
      }
    },
    [selectedOptions, onChange, isMulti, closeMenuOnSelect, rerenderMenuOnSelect, toggleOpen]
  );

  const handleClearFilter = useCallback(() => {
    onRemoveSearchTerm();
    onChange([]);
  }, [onChange, onRemoveSearchTerm]);

  // Register click outside event listener
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Update static options prop to state when not async
  useEffect(() => {
    if (!isAsync) {
      setMenuOptions(options);
    }
  }, [options, isAsync]);

  // Each time the dropdown is open, rebuild the menu options
  useEffect(() => {
    if (isMulti && isOpen) {
      onRemoveSearchTerm();
      setMenuOptions(isAsync ? getFullMenuOptions(menuOptions) : menuOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // By deault, search for all options, backend should handle the limitaion of
  // the default search without any search term, e.g. return top 10 results.
  // This should only be called once
  useEffect(() => {
    if (!isAsync) return;

    const fetchData = async () => {
      try {
        const results: OptionType[] = await loadOptions(searchTerm);
        setMenuOptions(getFullMenuOptions(results));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Error fetching options', error);
      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search for options when a search term is provided
  useEffect(() => {
    if (!isAsync) return;

    const fetchData = async () => {
      try {
        const results: OptionType[] = await loadOptions(searchTerm);
        if (searchTerm) {
          setMenuOptions(results);
        } else {
          setMenuOptions(getFullMenuOptions(results));
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Error fetching options', error);
      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Button label display values
  const {
    hasSelectedOptions,
    firstSelectedOption,
    hasButtonBadge,
    buttonBadgeValue,
  } = useMemo(() => {
    const _hasSelectedOptions: boolean = selectedOptions.length > 0 && !staticTrigger;
    const _firstSelectedOption: string =
      (isAsync ? getFullMenuOptions(menuOptions) : menuOptions).find(
        x => x.value === selectedOptions[0]
      )?.label || '';
    const _hasButtonBadge: boolean = selectedOptions.length > 1;
    const _buttonBadgeValue: number = selectedOptions.length - 1;
    return {
      hasSelectedOptions: _hasSelectedOptions,
      firstSelectedOption: _firstSelectedOption,
      hasButtonBadge: _hasButtonBadge,
      buttonBadgeValue: _buttonBadgeValue,
    };
  }, [selectedOptions, isAsync, getFullMenuOptions, menuOptions, staticTrigger]);

  return (
    <div ref={dropdownRef}>
      <DropdownMenu
        shouldRenderToParent
        isOpen={isOpen}
        trigger={({ triggerRef, ...props }) => (
          <div ref={triggerRef as LegacyRef<HTMLDivElement>}>
            <Button
              {...props}
              isSelected={isOpen || hasSelectedOptions}
              onClick={() => {
                setIsOpen(!isOpen);
              }}
              appearance={appearance as any}
            >
              <span css={[flexStart, spaceGapMinimum]}>
                <span>
                  <span css={hasSelectedOptions ? labelSelectedStyles : ''}>{label}</span>
                  {hasSelectedOptions && (
                    <span css={labelSelectedTitleStyles}>{`: ${firstSelectedOption}`}</span>
                  )}
                </span>
                {hasButtonBadge && !staticTrigger && (
                  <Badge max={Infinity} appearance="primary">
                    +{buttonBadgeValue}
                  </Badge>
                )}
                {iconAfter}
              </span>
            </Button>
          </div>
        )}
      >
        <div
          css={[
            advancedDropdownMenuStyles,
            menuWidth
              ? css`
                  width: ${menuWidth}px;
                `
              : '',
            menuHeight
              ? css`
                  height: ${isNumber(menuHeight) ? `${menuHeight}px` : menuHeight};
                `
              : '',
          ]}
        >
          {/* For now only show the search bar if the dropdown is aysnc and searchable */}
          {isAsync && isSearchable && (
            <div css={advancedDropdownSearchStyles} ref={searchRef}>
              <TextField
                autoComplete="off"
                onChange={(e: any) => {
                  e.currentTarget.value = e.currentTarget.value.replace(/ /g, '');
                  setSearchTerm(e.currentTarget.value);
                }}
                placeholder={placeholder ?? 'Search...'}
                elemAfterInput={
                  searchTerm ? (
                    <i
                      onClick={e => {
                        e.stopPropagation();
                        onRemoveSearchTerm();
                      }}
                    >
                      <SelectClearIcon
                        label=""
                        size="small"
                        primaryColor={token('color.icon.subtle')}
                      />
                    </i>
                  ) : null
                }
              />
            </div>
          )}
          {isLoading && <div css={[flexCenter, spaceTopCozy, secondaryTextStyles]}>Loading...</div>}
          {!isLoading && (
            <ul>
              {menuOptions.map(x => (
                <li key={x.value}>
                  {isMulti ? (
                    <Checkbox
                      onClick={e => {
                        e.stopPropagation();
                        handleOptionClick(`${x.value}`);
                      }}
                      label={x.label}
                      value={`${x.value}`}
                      isChecked={selectedOptions.includes(`${x.value}`)}
                    />
                  ) : (
                    <Radio
                      value={`${x.value}`}
                      label={x.label}
                      isChecked={selectedOptions.includes(`${x.value}`)}
                      onClick={e => {
                        e.stopPropagation();
                        handleOptionClick(`${x.value}`);
                      }}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
          {selectedOptions.length > 0 && !hideResetButton && (
            <div css={clearFilterStyles} onClick={handleClearFilter}>
              {labelReset}
            </div>
          )}
        </div>
      </DropdownMenu>
    </div>
  );
};
export default AdvancedDropdown;
